(() => {
  const OUT_CLASS = 'product-fast-nav--out';
  const ACTIVE_CLASS = 'is-active';
  const MQ_MOBILE = '(max-width: 768.98px)';
  const TAB_TARGETS = {
    specifications: 'panel-4',
    reviews: 'panel-5',
    performance: 'panel-6',
  };
  const MAIN_TARGETS = ['upgrades', 'peripherals', 'finance'];
  const TAB_TARGET_KEYS = Object.keys(TAB_TARGETS);
  const TARGET_SELECTOR = '[data-pdp-nav-target]';
  const scrollOpts = { passive: true };
  const mqMobile = window.matchMedia(MQ_MOBILE);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new WeakMap();
  const activeInstances = new Set();

  const isMobileViewport = () => mqMobile.matches;

  function setScrolledToBottom() {
    document.body.classList.toggle(
      'scrolled-to-bottom',
      window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
    );
  }

  function getElementTop(element) {
    return element.getBoundingClientRect().top + window.scrollY;
  }

  function getVisibleRect(element) {
    if (!element || !element.getClientRects().length) return null;
    return element.getBoundingClientRect();
  }

  class ProductFastNav {
    constructor(nav) {
      this.nav = nav;
      this.section = nav.closest('.cc-main-product');
      this.product = this.section ? this.section.querySelector('.product') : null;
      this.productForm = this.section ? this.section.querySelector('product-form') : null;
      this.scroller = nav.querySelector('.product-fast-nav__scroll');
      this.links = Array.from(nav.querySelectorAll('[data-pdp-fast-nav-link]'));
      this.frame = null;
      this.refreshFrame = null;
      this.visibilityObserver = null;
      this.productObserver = null;
      this.tabObserver = null;
      this.tabsElement = null;
      this.activeKey = '';
      this.activeLockUntil = 0;

      this.handleClick = this.handleClick.bind(this);
      this.scheduleUpdate = this.scheduleUpdate.bind(this);
      this.scheduleRefresh = this.scheduleRefresh.bind(this);
      this.updateActive = this.updateActive.bind(this);

      this.bind();
    }

    bind() {
      if (!this.section) return;

      this.nav.addEventListener('click', this.handleClick);
      window.addEventListener('scroll', this.scheduleUpdate, scrollOpts);
      window.addEventListener('resize', this.scheduleUpdate);
      window.addEventListener('on:breakpoint-change', this.scheduleUpdate);

      if (typeof mqMobile.addEventListener === 'function') {
        mqMobile.addEventListener('change', this.scheduleUpdate);
      } else {
        mqMobile.addListener(this.scheduleUpdate);
      }

      if (this.productForm && 'IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(this.scheduleUpdate);
        this.visibilityObserver.observe(this.productForm);
      }

      if (this.product && 'MutationObserver' in window) {
        this.productObserver = new MutationObserver(this.scheduleRefresh);
        this.productObserver.observe(this.product, { childList: true, subtree: true });
      }

      this.refreshTargets();
      this.updateVisibility();
      this.updateActive();
    }

    destroy() {
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
      this.nav.removeEventListener('click', this.handleClick);
      window.removeEventListener('scroll', this.scheduleUpdate, scrollOpts);
      window.removeEventListener('resize', this.scheduleUpdate);
      window.removeEventListener('on:breakpoint-change', this.scheduleUpdate);

      if (typeof mqMobile.removeEventListener === 'function') {
        mqMobile.removeEventListener('change', this.scheduleUpdate);
      } else {
        mqMobile.removeListener(this.scheduleUpdate);
      }

      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.productObserver) this.productObserver.disconnect();
      if (this.tabObserver) this.tabObserver.disconnect();
    }

    scheduleUpdate() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateVisibility();
        this.updateActive();
      });
    }

    scheduleRefresh() {
      if (this.refreshFrame) return;
      this.refreshFrame = requestAnimationFrame(() => {
        this.refreshFrame = null;
        this.refreshTargets();
      });
    }

    updateVisibility() {
      setScrolledToBottom();

      if (!isMobileViewport() || !this.productForm) {
        this.nav.classList.add(OUT_CLASS);
        return;
      }

      this.nav.classList.toggle(OUT_CLASS, this.productForm.getBoundingClientRect().bottom > 0);
    }

    refreshTargets() {
      this.tabsElement = this.getTabsElement();
      this.observeTabs();
      this.refreshLinks();
      this.updateActive();
    }

    hasTarget(key) {
      if (!key) return false;

      if (TAB_TARGET_KEYS.includes(key)) {
        const tabs = this.getTabsElement();
        if (!tabs) return false;

        const panelId = TAB_TARGETS[key];
        return !!(
          tabs.querySelector(`[data-pdp-tab-target="${key}"]`) ||
          tabs.querySelector(`[role="tab"][aria-controls="${panelId}"]`) ||
          tabs.querySelector(`[data-pdp-nav-target="${key}"]`)
        );
      }

      return !!this.getMainTarget(key);
    }

    refreshLinks() {
      this.links.forEach((link) => {
        // Hide any link whose destination is absent (e.g. a product without a
        // peripherals group or a missing tab) so the nav only lists real jumps.
        link.hidden = !this.hasTarget(link.dataset.pdpNavTarget);
      });
    }

    observeTabs() {
      if (this.tabObserver) this.tabObserver.disconnect();
      if (!this.tabsElement || !('MutationObserver' in window)) return;

      this.tabObserver = new MutationObserver(this.updateActive);
      this.tabObserver.observe(this.tabsElement, {
        attributes: true,
        attributeFilter: ['aria-selected'],
        subtree: true,
      });
    }

    handleClick(event) {
      const link = event.target.closest('[data-pdp-fast-nav-link]');
      if (!link || !this.nav.contains(link)) return;

      const key = link.dataset.pdpNavTarget;
      if (!key) return;

      const target = this.getScrollTarget(key);
      if (!target) return;

      event.preventDefault();

      // Keep the clicked link highlighted through the smooth-scroll animation
      // instead of letting intermediate scroll positions flicker the underline.
      this.activeLockUntil = reducedMotion.matches ? 0 : Date.now() + 700;
      this.setActiveLink(key);

      if (TAB_TARGET_KEYS.includes(key)) {
        this.activateTab(key);
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.scrollToTarget(target);
          this.setActiveLink(key);
        });
      });
    }

    getScrollTarget(key) {
      if (TAB_TARGET_KEYS.includes(key)) {
        return this.getTabsElement() || this.findDetailsSection();
      }

      return this.getMainTarget(key);
    }

    queryTarget(selector) {
      if (!this.section) return null;

      const matches = this.section.querySelectorAll(selector);
      for (let i = 0; i < matches.length; i += 1) {
        // The nav links themselves carry `data-pdp-nav-target`, so a plain
        // section query would resolve to the fixed navigation instead of the
        // content. Skip anything inside the nav.
        if (!this.nav.contains(matches[i])) return matches[i];
      }

      return null;
    }

    getMainTarget(key) {
      if (!this.section) return null;

      if (key === 'peripherals') {
        return (
          this.queryTarget('[data-pdp-nav-target="peripherals"]') ||
          this.queryTarget('.tpo_option-group_wrapper.show.m-easify-accessories-group') ||
          this.queryTarget('.tpo_option-group_wrapper.m-easify-accessories-group')
        );
      }

      if (key === 'finance') {
        const target = this.queryTarget('[data-pdp-nav-target="finance"]');
        if (target) return target;

        const financePlacement = this.queryTarget(
          '#custom-paypal-placement, #klarna-osm, dopplepay-widget, shopify-payment-terms'
        );
        return financePlacement
          ? financePlacement.closest('.product-info__block') || financePlacement
          : null;
      }

      if (key === 'upgrades') {
        return (
          this.queryTarget('[data-pdp-nav-target="upgrades"]') ||
          this.queryTarget('#easify-options') ||
          this.queryTarget('.tpo_option-group_wrapper')
        );
      }

      return this.queryTarget(`${TARGET_SELECTOR}[data-pdp-nav-target="${key}"]`);
    }

    findDetailsSection() {
      let sibling = this.section ? this.section.nextElementSibling : null;

      while (sibling && !sibling.matches('.cc-main-product')) {
        if (sibling.matches('.cc-product-details')) return sibling;
        sibling = sibling.nextElementSibling;
      }

      return null;
    }

    getTabsElement() {
      const details = this.findDetailsSection();
      return details ? details.querySelector('[data-pdp-nav-tabs], tabbed-content') : null;
    }

    activateTab(key) {
      const tabs = this.getTabsElement();
      if (!tabs) return;

      const panelId = TAB_TARGETS[key];
      const tab =
        tabs.querySelector(`[data-pdp-tab-target="${key}"]`) ||
        tabs.querySelector(`[role="tab"][aria-controls="${panelId}"]`);

      if (!tab || tab.getAttribute('aria-selected') === 'true') return;

      tab.click();

      if (tab.getAttribute('aria-selected') !== 'true') {
        this.setTabState(tabs, tab);
      }
    }

    setTabState(tabs, activeTab) {
      tabs.querySelectorAll('[role="tab"]').forEach((tab) => {
        const isActive = tab === activeTab;
        tab.setAttribute('aria-selected', isActive);
        if (isActive) {
          tab.removeAttribute('tabindex');
        } else {
          tab.setAttribute('tabindex', '-1');
        }

        const panelId = tab.getAttribute('aria-controls');
        const panel = panelId ? tabs.querySelector(`#${panelId}`) : null;
        if (panel) panel.hidden = !isActive;
      });
    }

    getActiveTabKey() {
      const tabs = this.getTabsElement();
      if (!tabs) return 'specifications';

      const activeTab = tabs.querySelector('[role="tab"][aria-selected="true"]');
      if (activeTab && activeTab.dataset.pdpTabTarget) return activeTab.dataset.pdpTabTarget;

      const visiblePanel = tabs.querySelector('[role="tabpanel"]:not([hidden])');
      return visiblePanel && visiblePanel.dataset.pdpNavTarget
        ? visiblePanel.dataset.pdpNavTarget
        : 'specifications';
    }

    getStickyOffset() {
      const navRect = this.nav.classList.contains(OUT_CLASS)
        ? null
        : this.nav.getBoundingClientRect();
      const header = document.querySelector('store-header[data-is-sticky="true"]');
      const headerRect = header ? header.getBoundingClientRect() : null;
      const navBottom = navRect ? Math.max(0, navRect.bottom) : 0;
      const headerBottom =
        headerRect && !header.classList.contains('m-header-hidden')
          ? Math.max(0, headerRect.bottom)
          : 0;

      return Math.ceil(Math.max(navBottom, headerBottom) + 8);
    }

    getNavHeight() {
      return this.nav.offsetHeight || 0;
    }

    getStickyHeaderHeight() {
      const header = document.querySelector('store-header[data-is-sticky="true"]');
      if (!header || !isMobileViewport()) return 0;

      const raw = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      );
      return Number.isFinite(raw) ? raw : header.getBoundingClientRect().height;
    }

    scrollToTarget(target) {
      const elementTop = getElementTop(target);
      const navOffset = this.getNavHeight() + 8;

      // Scrolling upward re-reveals the auto-hiding mobile header, which pushes
      // the fixed fast-nav down by the header height. Reserve that space up
      // front so the destination lands just below the nav instead of behind it.
      const goingUp = elementTop - navOffset < window.scrollY - 2;
      const headerOffset = goingUp ? this.getStickyHeaderHeight() : 0;
      const top = Math.max(0, elementTop - navOffset - headerOffset);

      window.scrollTo({
        top,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    getCurrentActiveKey() {
      const candidates = [];

      MAIN_TARGETS.forEach((key) => {
        const element = this.getMainTarget(key);
        if (getVisibleRect(element)) {
          candidates.push({ key, element, top: getElementTop(element) });
        }
      });

      const tabs = this.getTabsElement();
      if (getVisibleRect(tabs)) {
        candidates.push({
          key: this.getActiveTabKey(),
          element: tabs,
          top: getElementTop(tabs),
        });
      }

      if (!candidates.length) return '';

      candidates.sort((a, b) => a.top - b.top);

      const line = window.scrollY + this.getStickyOffset() + 24;
      let current = candidates[0];

      candidates.forEach((candidate) => {
        if (candidate.top <= line) {
          current = candidate;
        }
      });

      return current.key;
    }

    updateActive() {
      if (Date.now() < this.activeLockUntil) return;
      this.setActiveLink(this.getCurrentActiveKey());
    }

    setActiveLink(key) {
      if (!key) return;

      this.links.forEach((link) => {
        const isActive = link.dataset.pdpNavTarget === key;
        link.classList.toggle(ACTIVE_CLASS, isActive);
        link.toggleAttribute('aria-current', isActive);
      });

      if (this.activeKey === key) return;
      this.activeKey = key;

      this.centerActiveLink(key);
    }

    centerActiveLink(key) {
      const activeLink = this.links.find((link) => link.dataset.pdpNavTarget === key);
      if (!activeLink || !this.scroller) return;
      if (!isMobileViewport() || this.nav.classList.contains(OUT_CLASS)) return;

      const scrollerRect = this.scroller.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();
      const delta =
        linkRect.left + linkRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2);

      // Use scrollTo with an absolute clamped target instead of scrollBy so that
      // Safari's overflow-x container never receives a negative scrollLeft, which
      // creates the large empty gap on the left of the nav bar seen on mobile.
      this.scroller.scrollTo({
        left: Math.max(0, this.scroller.scrollLeft + delta),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }
  }

  function init(root = document) {
    const navs = [];
    if (root.matches && root.matches('.product-fast-nav')) navs.push(root);
    navs.push(...root.querySelectorAll('.product-fast-nav'));

    navs.forEach((nav) => {
      if (nav.__mProductFastNavController || instances.has(nav)) return;
      const instance = new ProductFastNav(nav);
      nav.__mProductFastNavController = instance;
      instances.set(nav, instance);
      activeInstances.add(instance);
    });
  }

  function destroy(root) {
    const navs = [];
    if (root.matches && root.matches('.product-fast-nav')) navs.push(root);
    navs.push(...root.querySelectorAll('.product-fast-nav'));

    navs.forEach((nav) => {
      const instance = nav.__mProductFastNavController || instances.get(nav);
      if (!instance) return;
      instance.destroy();
      delete nav.__mProductFastNavController;
      instances.delete(nav);
      activeInstances.delete(instance);
    });
  }

  function refreshAll() {
    activeInstances.forEach((instance) => instance.scheduleRefresh());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    refreshAll();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
    refreshAll();
  });
  document.addEventListener('on:bfcache:load-restore', () => init(), { once: true });
})();
